import {Component, Inject, OnInit} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RequestService } from '../../services/api/v1/request.service';
import { AuthService } from '../../services/api/v1/auth.service';
import { LocalStorageService, ResponseHttpSuccess, CustomMessageService, OriginMessageError } from 'library';
import { LocalStorageKey } from '../../enums';
import { HttpStatusCode } from '@angular/common/http';
import {DOCUMENT} from "@angular/common";

@Component({
  selector: 'app-layout-iframe',
  templateUrl: './app.layout-iframe.component.html',
  styles: [`
    .iframe-layout {
      width: 100%;
      height: 100vh;
      padding: 0;
      margin: 0;
      background: #fff;
      overflow-x: hidden;
    }

    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `]
})
export class AppLayoutIframeComponent implements OnInit {
  isReady = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private route: ActivatedRoute,
    private requestService: RequestService,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private customMessageService: CustomMessageService) {
    let themeLink = this.document.getElementById('app-theme') as HTMLLinkElement;
    if (themeLink) {
      themeLink.href = 'krediya.v2-theme.css';
    }
  }

  async ngOnInit() {
    // Extract flow code and category code from route params
    const flowCode = this.route.snapshot.params['flow_code'];
    const categoryCode = this.route.snapshot.params['category_code'];
    const startToken = this.route.snapshot.queryParams['start_token'];

    // Add UTM parameters to current URL if not present
    this.addUtmParametersToUrl();

    // Process start_token if provided (same as SalesComponent)
    if (startToken) {
      try {
        const decodedToken = JSON.parse(atob(startToken));
        this.localStorageService.set(LocalStorageKey.startToken, decodedToken);
        console.log('Start token processed:', decodedToken);

        // Get sales token from API and WAIT for it - passing decoded token directly
        await this.getSalesToken(decodedToken);
      } catch (error) {
        this.customMessageService.responseNoticeHandler({message: 'Error processing start_token:', error: error}, OriginMessageError.FRONTEND)
      }
    }

    // Store flow_code if needed (you can add a property in requestService if required)
    if (flowCode) {
      console.log('Flow Code:', flowCode);
      // this.requestService.flowCode = flowCode; // Add this property to RequestService if needed
    }

    if (categoryCode) {
      // If category code is provided, it might be the category ID directly
      // Check if it's a UUID format (category ID) or a code that needs conversion
      if (this.isUUID(categoryCode)) {
        // If it's already a UUID, use it directly as categoryId
        this.requestService.categoryId = categoryCode;
      } else {
        // If it's a code, convert it to category ID
        this.requestService.categoryId = this.getCategoryIdByCode(categoryCode);
      }
    } else {
      // Set default category ID when no category is provided
      this.requestService.categoryId = '0ee82679-0154-11ee-ba26-42010a8a0002';
    }

    // Mark as ready to render
    this.isReady = true;
  }

  private getSalesToken(decodedToken?: any): Promise<void> {
    const tokenData = this.buildTokenData(decodedToken);

    console.log('Calling getToken with data:', tokenData);

    return new Promise((resolve, reject) => {
      this.authService.getToken(tokenData).subscribe({
        next: (resp: ResponseHttpSuccess) => {
          if (resp.status === HttpStatusCode.Ok && resp.body?.data?.sales_token) {
            this.localStorageService.set(LocalStorageKey.salesToken, resp.body.data.sales_token);
            console.log('Sales token obtained successfully');
            resolve();
          } else {
            this.customMessageService.logErrorOnly(resp);
            reject(new Error('Failed to get sales token'));
          }
        },
        error: (error) => {
          this.customMessageService.logErrorOnly(error);
          reject(error);
        }
      });
    });
  }

  private buildTokenData(decodedToken?: any): { store_id: string; source: string; current_token: string } {
    // Use the provided decoded token first, otherwise try to get from localStorage
    const startToken = decodedToken || this.localStorageService.get(LocalStorageKey.startToken);
    const currentToken = this.localStorageService.get(LocalStorageKey.salesToken) || '';

    if (startToken) {
      return {
        store_id: startToken.store_id || '',
        source: startToken.source || '',
        current_token: currentToken
      };
    }

    return {
      store_id: '',
      source: '',
      current_token: currentToken
    };
  }

  private getCategoryIdByCode(code: string): string {
    // Map category codes to their UUIDs
    const categoryMap: { [key: string]: string } = {
      'kredicash': '0ee82679-0154-11ee-ba26-42010a8a0002',
      // Add more mappings as needed
    };

    return categoryMap[code] || '0ee82679-0154-11ee-ba26-42010a8a0002'; // Default to kredicash if not found
  }

  private isUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  private addUtmParametersToUrl(): void {
    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;

    // Check if UTM parameters are already present
    if (!params.has('utm_source')) {
      // Add UTM parameters
      params.append('utm_source', 'facebook');
      params.append('utm_medium', 'conversiones');
      params.append('utm_campaign', 'PA-KYA-CON-202406-CONVERSIONES-ZEGMENTA-+4+BBDD-001');
      params.append('utm_id', 'kredicash');
      params.append('utm_term', 'zegmenta');

      // Update the URL without reloading the page
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }
}
